import { create } from 'zustand';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from '@/lib/firebase/firestore';
import type { DocumentSnapshot, QueryDocumentSnapshot } from 'firebase/firestore';
import { ref, onValue, off } from 'firebase/database';
import type { DatabaseReference } from 'firebase/database';
import { httpsCallable, callServiceAPI } from '@/lib/firebase/functions';
import { db, rtdb, functions } from '@/config/firebase';
import { SAAS_ALIASES } from '@/config/constants';
import { openWindowSafe } from '@/lib/utils/popup';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STRIPE_PUBLISHABLE_KEY = 'pk_live_vZ8o9dQoM2zharvDfdExWgZf00GIVF1VvZ';

const TAX_RATES_INR = ['txr_1Hv2S0Ho9p6j6FgR0qRizWPw'];
const TAX_RATES_OTHER = ['txr_1IlvlbHo9p6j6FgRG8iwtor9'];

const CURRENCY_MAP: Record<string, string> = {
  inr: '\u20B9',
  usd: '$',
  eur: '\u20AC',
  gbp: '\u00A3',
  aud: 'A$',
};

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface Product {
  id: string;
  name: string;
  description?: string;
  images: string[];
  active: boolean;
  stripe_metadata_payment_page_order?: string;
  stripe_metadata_company?: string;
  stripe_metadata_appcode?: string;
  stripe_metadata_per_user_price?: string;
  stripe_metadata_amount_per_display?: string;
  stripe_metadata_userIdText?: string;
  [key: string]: any;
}

export interface Price {
  id: string;
  productId: string;
  active: boolean;
  currency: string;
  unit_amount: number;
  type: string;
  interval?: string;
  /** Stripe `recurring.interval_count`: 1 = monthly, 3 = every 3 months. */
  interval_count?: number;
  description?: string;
  trial_period_days?: number;
  nickname?: string;
}

/** Stripe product "Ulgebra Credits". */
export const ULGEBRA_CREDITS_PRODUCT_ID = 'prod_MGbKLzmgcTnb40';

/**
 * The product-level `amount_per_display` note, shown as-is for every price —
 * except the two monthly Ulgebra Credits prices (1K INR and 1K USD,
 * interval_count = 1). That product's note is "renews /3month, not", which is
 * only true for its 3-month prices; the monthly ones get a plain "renews" so
 * the card reads "/ renews / month". A price whose doc lacks interval_count
 * keeps the note (fail safe). Nothing else is affected.
 */
export function periodNoteFor(
  product: Pick<Product, 'id' | 'stripe_metadata_amount_per_display'>,
  price: Pick<Price, 'interval_count'>,
): string | undefined {
  const note = product.stripe_metadata_amount_per_display;
  if (!note) return undefined;
  const isUlgebraCreditsMonthly =
    product.id === ULGEBRA_CREDITS_PRODUCT_ID && price.interval_count === 1;
  return isUlgebraCreditsMonthly ? 'renews' : note;
}

/**
 * One feature bullet of a plan card. Ulgebra Credits' Stripe bullets start
 * with a tiny "~" for approximate message counts; show a clearer "≈" for that
 * product only. Every other product's bullets are returned as written.
 * Used by the Store page and the Settings plans browser so both agree.
 */
export function featureLineFor(product: Pick<Product, 'id'>, line: string): string {
  const trimmed = line.trim();
  return product.id === ULGEBRA_CREDITS_PRODUCT_ID
    ? trimmed.replace(/^[~˜∼]\s*/, '≈ ')
    : trimmed;
}

/**
 * Period shown after the Total amount on the quantity step. Ulgebra Credits
 * multi-month prices (interval_count = 3) read "3 months" instead of "month";
 * every other product/price keeps the bare Stripe interval.
 */
export function totalPeriodFor(
  product: Pick<Product, 'id'>,
  price: Pick<Price, 'interval' | 'interval_count'>,
): string | undefined {
  const count = price.interval_count ?? 1;
  if (product.id === ULGEBRA_CREDITS_PRODUCT_ID && count > 1 && price.interval) {
    return `${count} ${price.interval}s`;
  }
  return price.interval;
}

export interface Subscription {
  id: string;
  status: string;
  quantity: number;
  product: any;
  price: any;
  created: any;
  current_period_start?: any;
  current_period_end?: any;
  canceled_at?: any;
  productData?: Product;
  priceData?: Price;
  productId?: string;
  priceId?: string;
  /**
   * Set only on subscriptions that belong to ANOTHER account the signed-in
   * user was granted access to (see `delegatedSubscriptions`). Undefined on
   * the user's own subs. Every mutation must target this uid, not the
   * signed-in uid, or the write lands on the wrong customer.
   */
  ownerUid?: string;
  /** Email of the granting account — shown on the card so the source is obvious. */
  ownerEmail?: string;
}

export interface LicenseOwner {
  userIds: string[];
}

/**
 * Another Ulgebra account whose subscriptions the signed-in user may view and
 * manage. Granted by the Ulgebra team via `UASubscriptionDelegates/{ownerUid}`
 * and resolved by the `getDelegatedSubscriptionOwners` Cloud Function — used
 * for display only, the server re-verifies every write.
 */
export interface DelegatedOwner {
  ownerUid: string;
  ownerEmail: string;
}

export interface CreditActivity {
  ct: number;
  old: number;
  new: number;
  type?: string;
  hint?: string;
  oldStatus?: string;
  newStatus?: string;
  oldSub?: any;
  newSub?: any;
}

export interface CRMUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Per-user setting: the credits granted by each subscription billing cycle
 * are automatically credited to `transferToEmail`'s Ulgebra account. Stored
 * at `customers/{uid}/creditsTransferTo/default` (managed via
 * new_callServiceAuthorizedAPI) — the payment-succeeded webhook reads the
 * same doc when granting credits.
 */
export interface RenewalCreditTransferConfig {
  enabled: boolean;
  transferToEmail: string;
}

export interface SubscriptionDelegate {
  uid: string;
  email: string;
}

type CRMUsersStatus = 'idle' | 'loading' | 'loaded' | 'no_connection' | 'error';

// ---------------------------------------------------------------------------
// CRM Users API config — URL + response normalizer per CRM
// Auth headers are injected automatically by new_callServiceAuthorizedAPI
// ---------------------------------------------------------------------------

const CRM_USERS_CONFIG: Record<string, {
  url: string | (() => string);
  authHeader: string;
  /** HTTP method — default 'GET'. Use 'POST' for GraphQL APIs (e.g. Monday.com) */
  method?: string;
  /** POST body — required when method is 'POST' (e.g. GraphQL query) */
  data?: unknown;
  normalize: (data: any) => CRMUser[];
}> = {
  zohocrm: {
    url: 'https://www.zohoapis.com/crm/v2/users?type=ActiveUsers&per_page=200',
    authHeader: 'Zoho-oauthtoken {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: u.full_name || u.name || '', email: u.email })),
  },
  hubspotcrm: {
    url: 'https://api.hubapi.com/crm/v3/owners?archived=false&limit=500',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.results || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({
          id: String(u.id),
          name: [u.firstName, u.lastName].filter(Boolean).join(' '),
          email: u.email,
        })),
  },
  pipedrive: {
    // Server prepends api_domain to the URL
    url: '/v1/users',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.data || [])
        .filter((u: any) => u.email && u.active_flag)
        .map((u: any) => ({ id: String(u.id), name: u.name || '', email: u.email })),
  },
  freshworkscrm: {
    // Server prepends https://{api_domain} to the URL
    url: '/crm/sales/api/selector/owners',
    authHeader: 'Token token={{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: u.display_name || '', email: u.email })),
  },
  freshdesk: {
    // Server prepends https://{api_domain} to the URL
    url: '/api/v2/agents?per_page=100',
    authHeader: 'Basic {{AUTHTOKEN}}',
    normalize: (data) =>
      (Array.isArray(data) ? data : [])
        .filter((a: any) => a?.contact?.email)
        .map((a: any) => ({ id: String(a.id), name: a.contact?.name || '', email: a.contact.email })),
  },
  bitrix24: {
    // Server prepends https://{api_domain} to the URL
    url: '/rest/user.get.json?filter[ACTIVE]=true',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.result || [])
        .filter((u: any) => u.EMAIL)
        .map((u: any) => ({
          id: String(u.ID),
          name: [u.NAME, u.LAST_NAME].filter(Boolean).join(' '),
          email: u.EMAIL,
        })),
  },
  monday: {
    // Monday.com uses GraphQL API — requires POST method
    // Matches original addSAASUsersToLICUtility: users(limit:100) query
    url: 'https://api.monday.com/v2',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    method: 'POST',
    data: {
      query: '{ users(limit: 100) { id name email phone is_admin is_guest is_view_only account { name id slug } } }',
    },
    normalize: (data: any) =>
      (data?.data?.users || [])
        .filter((u: any) => u.email && !u.is_guest && !u.is_view_only)
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
        .map((u: any) => ({
          id: String(u.id),
          name: u.name || '',
          email: u.email,
        })),
  },
  zohodesk: {
    // Extension: twilioforzohodesk → saas = "zohodesk"
    url: 'https://desk.zoho.com/api/v1/agents?status=ACTIVE&limit=200',
    authHeader: 'Zoho-oauthtoken {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.data || [])
        .filter((a: any) => a.emailId && a.status === 'ACTIVE' && a.isConfirmed)
        .map((a: any) => ({ id: String(a.id), name: a.name || '', email: a.emailId })),
  },
  zohobigin: {
    // Extension: twilioforzohobigin → saas = "zohobigin"
    url: 'https://www.zohoapis.com/bigin/v1/users?type=ActiveUsers&per_page=200',
    authHeader: 'Zoho-oauthtoken {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: u.full_name || u.name || '', email: u.email })),
  },
  zohorecruit: {
    // Extension: twilioforzohorecruit → saas = "zohorecruit"
    url: 'https://recruit.zoho.com/recruit/v2/users?type=ActiveUsers&per_page=200',
    authHeader: 'Zoho-oauthtoken {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: u.full_name || u.name || '', email: u.email })),
  },
  zohobooks: {
    // Zoho Books requires organization_id and uses the SaaS module's OAuth proxy.
    // loadCRMUsers has a special path for 'zohobooks' that calls saasModule.getOrgUsers()
    // instead of the cloud function — see the zohobooks branch in loadCRMUsers below.
    url: '__use_saas_module__',
    authHeader: '',
    normalize: (data) =>
      (data || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: u.name || '', email: u.email })),
  },
  // Note: zoho-campaigns does NOT have addSAASUsersToLICUtility in old code (commented out)
  salesforce: {
    // Old: SOQL query select name,email,profile.name,smallphotourl,id,phone from user
    url: '/services/data/v57.0/query?q=SELECT+Id,Name,Email,Phone,SmallPhotoUrl+FROM+User+WHERE+IsActive=true+AND+Email!=null',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.records || [])
        .filter((u: any) => u.Email)
        .map((u: any) => ({ id: String(u.Id), name: u.Name || '', email: u.Email })),
  },
  shopify: {
    // Old: GraphQL staffMembers(first:100) query via POST.
    // loadCRMUsers uses GET — use REST endpoint as fallback.
    url: '/admin/api/2024-01/users.json',
    authHeader: 'X-Shopify-Access-Token {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: [u.first_name, u.last_name].filter(Boolean).join(' '), email: u.email })),
  },
  zendesksupport: {
    // Extension: twilioforzendesksupport → saas = "zendesksupport"
    url: '/api/v2/users?per_page=100&role[]=agent&role[]=admin',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email && !u.suspended)
        .map((u: any) => ({ id: String(u.id), name: u.name || '', email: u.email })),
  },
  zendesksell: {
    // Extension: twilioforzendesksell → saas = "zendesksell"
    url: 'https://api.getbase.com/v2/users?status=active',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.items || [])
        .filter((item: any) => item?.data?.email)
        .map((item: any) => ({ id: String(item.data.id), name: item.data.name || '', email: item.data.email })),
  },
  activecampaign: {
    // Old: /api/3/users → data.users → {email, firstName, lastName}
    url: '/api/3/users',
    authHeader: 'Basic {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id), name: [u.firstName, u.lastName].filter(Boolean).join(' '), email: u.email })),
  },
  dynamics: {
    // Old: /api/data/v9.2/systemusers?$filter=isdisabled eq false and issyncwithdirectory eq true and islicensed eq true
    url: '/api/data/v9.2/systemusers?$filter=isdisabled%20eq%20false%20and%20issyncwithdirectory%20eq%20true%20and%20islicensed%20eq%20true',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.value || [])
        .filter((u: any) => u.internalemailaddress)
        .map((u: any) => ({ id: String(u.systemuserid), name: u.fullname || '', email: u.internalemailaddress })),
  },
  leadsquared: {
    // Old: /v2/UserManagement.svc/Users.Get → data array → {ID, LastName, FirstName, EmailAddress}
    url: '/v2/UserManagement.svc/Users.Get',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (Array.isArray(data) ? data : [])
        .filter((u: any) => u.EmailAddress)
        .map((u: any) => ({ id: String(u.ID), name: [u.FirstName, u.LastName].filter(Boolean).join(' '), email: u.EmailAddress })),
  },
  odoo: {
    // Old: /ulgebra-odoo/users?limit=200 → data.users → {name, email}
    url: '/ulgebra-odoo/users?limit=200',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.users || [])
        .filter((u: any) => u.email)
        .map((u: any) => ({ id: String(u.id || u.user_id), name: u.name || '', email: u.email })),
  },
  slack: {
    // Old: users.list → data.members → filter bots → {id, real_name, profile.email}
    url: 'https://slack.com/api/users.list',
    authHeader: 'Bearer {{AUTHTOKEN}}',
    normalize: (data) =>
      (data?.members || [])
        .filter((u: any) => !u.is_bot && !u.is_restricted && !u.deleted && u.is_email_confirmed && u.profile?.email)
        .map((u: any) => ({ id: String(u.id), name: u.real_name || u.name || '', email: u.profile.email })),
  },
};

// ---------------------------------------------------------------------------
// OAuth Authorization config — auth URL + client IDs per CRM
// Used to open OAuth popup from the store page when CRM is not authorized
// ---------------------------------------------------------------------------

const OAUTH_AUTH_CONFIG: Record<string, {
  authUrl: string;
  clientIdMap: Record<string, string>;
  scopes?: string;
  /** Per-extension scope lists, used instead of `scopes` for those apps. */
  scopeOverrides?: Record<string, string>;
  /** Scope separator — HubSpot uses space, Zoho uses comma */
  scopeSeparator?: string;
  redirectUri: string;
  /** Whether to include client_id in redirect_uri (Pipedrive) */
  includeClientIdInRedirect?: boolean;
}> = {
  zohocrm: {
    authUrl: 'https://accounts.zoho.com/oauth/v2/auth',
    clientIdMap: {
      '*': '1000.BEJADNTOF3QA6RR0FD0MQ61VMNZLBK',
    },
    scopes: 'ZohoCRM.users.READ,ZohoCRM.settings.fields.READ,ZohoCRM.org.READ,ZohoCRM.modules.READ,ZohoCRM.modules.CREATE,ZohoCRM.modules.UPDATE,ZohoSearch.securesearch.READ,ZohoCRM.settings.custom_views.READ,ZohoCRM.settings.modules.READ',
    scopeSeparator: ',',
    redirectUri: 'https://sms.ulgebra.com/oauth-home',
  },
  hubspotcrm: {
    authUrl: 'https://app.hubspot.com/oauth/authorize',
    clientIdMap: {
      twilioforhubspotcrm: '28e3fcba-92f6-4301-8a82-7381f8b658a9',
      pinnacleforhubspotcrm: 'f30b42b5-9aca-43df-8da5-fc91ac136ed0',
      vonageforhubspotcrm: 'df4b5c87-6315-41eb-93a0-a59314df0a38',
      messagebirdforhubspotcrm: '9be1eb84-88d7-4bbe-972b-f507b913f504',
      clickatellforhubspotcrm: 'a1fc08b9-ceb5-4fff-98ef-6c1af7220e68',
      ringcentralforhubspotcrm: '91ef3af9-c7b9-40a7-bbe9-650fd7ab5323',
      textlocalforhubspotcrm: '217e8bc7-73b3-4b0b-9a2f-e5e8eb756f3d',
      karixforhubspotcrm: '18454e29-584d-422e-be15-a248b7d0af9b',
      rivetforhubspotcrm: 'f5b28c25-572a-4c91-a5fc-2f300dc4bdeb',
      routemobileforhubspotcrm: '19a87901-c92c-4901-9b96-d7775db63b59',
      stripeforhubspotcrm: '02b945fe-9cdb-445c-aec7-1f7b4cb5b200',
      whatcetraforhubspotcrm: '2b90ed06-f4fc-4ef7-bb1e-89346dea1461',
      whatsappforhubspotcrm: '5ce62ce4-e697-423c-b878-a2b28f9dd297',
      calendlyforhubspotcrm: 'e6c60ffe-b50a-4842-8a53-e67d95c94136',
      lookupforhubspotcrm: '870ca14e-3b45-4332-9636-32168ff05b12',
      grapevineforhubspotcrm: '5a189156-eb29-41e5-b3a9-43dd1cbe7cdc',
    },
    scopes: 'timeline crm.lists.read crm.objects.contacts.read crm.schemas.contacts.read crm.objects.companies.read crm.objects.deals.read crm.schemas.companies.read crm.schemas.deals.read crm.objects.owners.read',
    // Per-extension overrides. The shared string above is what most apps ask
    // for; an app listed here asks for its own set instead. LookUp's entry drops
    // timeline and crm.lists.read, which its HubSpot app config no longer
    // declares — the other apps here do still use timeline (see
    // MODULE_VS_TIMELINE_TEMPLATE_ID in modules/saas/hubspot), so the shared
    // string must keep it.
    //
    // This list is cosmetic as far as HubSpot is concerned: the consent screen
    // is built from the app's own requiredScopes, not from this parameter.
    // Verified against the live authorize endpoint for lookupforhubspotcrm
    // (11 requiredScopes) on 29 Sep 2026 — asking for these 7 still consented to
    // all 11, and asking for the old 19 consented to the same 11 with the
    // extras ignored. Neither direction errors, so a drift here is a
    // readability bug rather than a broken install.
    scopeOverrides: {
      lookupforhubspotcrm: 'crm.objects.contacts.read crm.schemas.contacts.read crm.objects.companies.read crm.objects.deals.read crm.schemas.companies.read crm.schemas.deals.read crm.objects.owners.read',
    },
    scopeSeparator: ' ',
    redirectUri: 'https://sms.ulgebra.com/oauth-home',
  },
  pipedrive: {
    authUrl: 'https://oauth.pipedrive.com/oauth/authorize',
    clientIdMap: {
      twilioforpipedrive: 'c8a6c923cf443ed6',
      whatsappforpipedrive: 'd688695f917bf352',
      messagebirdforpipedrive: '89e0cf9aefc3fdae',
      vonageforpipedrive: '8ae23112547cac40',
      clickatellforpipedrive: '75e9bb9450844c03',
      pinnacleforpipedrive: '7e31a48b6f3c4606',
      textlocalforpipedrive: '79eb4474d9705365',
      stripeforpipedrive: '3346bd3938a08aaa',
      lookupforpipedrive: '03e34563487a2340',
      calendlyforpipedrive: '1c850596d819c2d9',
      acuityschedulingforpipedrive: '00c72ed9d967386d',
      bulksmsforpipedrive: '25ceec49d103592f',
      anysmsforpipedrive: 'fd0ad0db79849432',
      burstsmsforpipedrive: 'f3457bb85a478efa',
    },
    redirectUri: 'https://sms.ulgebra.com/oauth-home',
    includeClientIdInRedirect: true,
  },
  monday: {
    authUrl: 'https://auth.monday.com/oauth2/authorize',
    clientIdMap: {
      twilioformonday: '633bc2175f36f0587fff54b491d124cd',
      twiliocallformonday: '633bc2175f36f0587fff54b491d124cd',
      messagebirdformonday: 'ac4500bd685445c20e2a203d114379bd',
      acuityschedulingformonday: '2afb57c1c827ae766cb8c5b7b100b5bd',
      intercomformonday: 'ab88cb555a07d222f17c0c3ddf86973a',
      googledriveformonday: '5bc6c25566f0112471dfcb45ef4920c8',
      calendlyformonday: 'b352f03b86647ef55fc3f9b48028b0da',
      ringcentralformonday: 'e04466f2d733faa032e31305cc2beeda',
      recurringtasksformonday: 'e44cbd8010c99f47cd0e80baa7556a46',
    },
    redirectUri: 'https://sms.ulgebra.com/oauth-home',
  },
  bitrix24: {
    // Bitrix24 uses a domain-based OAuth URL: https://{domain}/oauth/authorize/
    // The authUrl here is a placeholder — getOAuthURL builds the actual URL using the domain.
    authUrl: 'https://bitrix24.com/oauth/authorize/',
    clientIdMap: {
      pinnacleforbitrix24: 'app.628ca9f370b123.27655410',
      twilioforbitrix24: 'app.618df869835df8.59780487',
      twiliocallforbitrix24: 'app.618df869835df8.59780487',
      vonageforbitrix24: 'app.618df54baea5e6.35425096',
      clickatellforbitrix24: 'app.618df632e1e316.12060277',
      messagebirdforbitrix24: 'app.62e2625a500219.63455613',
      ringcentralforbitrix24: 'app.62bb49940a51d9.75471153',
      textlocalforbitrix24: 'app.62fe43e60fe999.41270105',
      whatcetraforbitrix24: 'app.5fca3756b0c5c2.28053584',
      whatsappforbitrix24: 'app.5fca3756b0c5c2.28053584',
      calendlyforbitrix24: 'app.618df3863830e4.72087882',
      acuityschedulingforbitrix24: 'app.618df22406aa95.14782903',
      stripeforbitrix24: 'app.618df22406aa95.14782903',
      lookupforbitrix24: 'app.63030c3eef5559.49583025',
    },
    redirectUri: 'https://sms.ulgebra.com/oauth-home',
  },
};

// ---------------------------------------------------------------------------
// Store state and actions
// ---------------------------------------------------------------------------

interface StoreState {
  // Data
  products: Product[];
  filteredProducts: Product[];
  prices: Record<string, Price[]>;
  subscriptions: Subscription[];
  licenseOwners: Record<string, LicenseOwner>;
  credits: number;
  creditHistory: CreditActivity[];
  renewalTransferConfig: RenewalCreditTransferConfig | null;
  /**
   * UID for which `loadRenewalTransferConfig` has completed (success or
   * error). Per-uid for the same reason as `licenseOwnersLoadedFor`: the
   * admin impersonation flow re-initializes the store for another uid and
   * the section must know the cached config belongs to the viewed user.
   */
  renewalTransferLoadedFor: string | null;
  /** User-facing reason the last `loadRenewalTransferConfig` failed (null = fine). */
  renewalTransferLoadError: string | null;
  /** Monotonic counter so a slow, superseded load can't stamp stale state. */
  _renewalTransferSeq: number;

  subscriptionDelegates: SubscriptionDelegate[];
  subscriptionDelegatesLoadedFor: string | null;
  subscriptionDelegatesLoadError: string | null;
  _subscriptionDelegatesSeq: number;
  savingSubscriptionDelegate: boolean;

  // Delegated access — subscriptions owned by OTHER accounts that the signed-in
  // user was granted access to (e.g. the employee who bought them has left the
  // company). Kept in their own arrays rather than merged into `subscriptions`
  // so `loadSubscriptions`, which replaces that array wholesale, can't wipe
  // them on a re-run. The store page renders own + delegated as one list.
  delegatedOwners: DelegatedOwner[];
  delegatedSubscriptions: Subscription[];
  delegatedLicenseOwners: Record<string, LicenseOwner>;
  loadingDelegated: boolean;
  /** Flips true after the grant lookup resolves (success OR failure). */
  delegatedLoaded: boolean;

  // Pagination
  creditHistoryLastDoc: DocumentSnapshot | null;
  creditHistoryHasMore: boolean;

  // UI state
  selectedProductId: string | null;
  selectedPaymentPlanId: string | null;
  quantity: number;
  selectedCurrency: string;
  searchTerm: string;

  // Loading states
  loadingProducts: boolean;
  /**
   * App-codes for which `loadProducts(appCode)` has completed at least once
   * (success OR error). Used to dedupe load attempts and to gate UI that
   * needs `productData` populated (e.g. trial paywall). A `loadProducts()`
   * call without an appCode adds the empty string ''.
   */
  productsLoadedForApps: Record<string, true>;
  loadingSubscriptions: boolean;
  subscriptionsLoaded: boolean;
  /**
   * UID for which `loadSubscriptions` last completed. Mirrors the
   * `licenseOwnersLoadedFor` pattern below: per-uid signal that lets
   * impersonation flows (dashboard, App Users, Subscription settings)
   * detect when the cached subs are for the wrong user and trigger a
   * fresh load. The boolean `subscriptionsLoaded` stays as-is for
   * back-compat with consumers that don't care which uid was loaded.
   */
  subscriptionsLoadedFor: string | null;
  loadingCredits: boolean;
  loadingCreditHistory: boolean;
  loadingLicenseOwners: boolean;
  /**
   * UID for which `loadLicenseOwners` has completed (success or error).
   * Per-uid (not a global boolean) so the dashboard's impersonation flow,
   * which calls `loadLicenseOwners(otherUid)`, isn't blocked when a load
   * for the current user already happened. A user with zero allocations
   * still flips this — consumers must gate on this rather than checking
   * `Object.keys(licenseOwners).length`, which would loop forever.
   */
  licenseOwnersLoadedFor: string | null;
  processingPayment: boolean;
  /** True after checkout opened in new tab — shows "I have paid" button */
  paymentPending: boolean;
  /** Last checkout URL for manual popup fallback */
  lastCheckoutUrl: string | null;
  processingLicenseUpdate: boolean;
  processingCreditTransfer: boolean;
  savingRenewalTransfer: boolean;
  processingPortal: boolean;

  // CRM user suggestions
  crmUsers: Record<string, CRMUser[]>;
  crmUsersStatus: Record<string, CRMUsersStatus>;

  // Error states
  error: string | null;

  // Internal refs for cleanup
  _creditsRef: DatabaseReference | null;
  /**
   * Bumped when a delegated load starts and again by `cleanup`. A load only
   * writes its results if the counter still matches the value it started
   * with, so a fetch in flight when the session changes (sign-out, admin
   * impersonation) is discarded instead of restoring the previous account's
   * subscriptions over the cleared state.
   */
  _delegatedRequestId: number;
  /** Impersonated uid the last delegated load was resolved for — reused by refreshes. */
  _delegatedImpersonUserId: string | null;

  // Actions
  initialize: (
    userId: string,
    preferredCurrency?: string,
    options?: { impersonateUserId?: string | null },
  ) => Promise<void>;
  detectCurrency: () => void;
  loadProducts: (appCode?: string) => Promise<void>;
  loadPricesForProduct: (productId: string) => Promise<void>;
  loadSubscriptions: (userId: string) => Promise<void>;
  enrichSubscriptions: () => void;
  loadCredits: (userId: string) => void;
  loadCreditHistory: (userId: string, loadMore?: boolean) => Promise<void>;
  loadLicenseOwners: (userId: string) => Promise<void>;
  /**
   * `impersonateUserId` (support only) resolves the grants of the account
   * the store page is impersonating instead of the signed-in user's own.
   */
  loadDelegatedAccess: (impersonateUserId?: string | null) => Promise<void>;
  selectProduct: (productId: string | null) => void;
  selectPaymentPlan: (priceId: string | null) => void;
  setQuantity: (quantity: number) => void;
  setCurrency: (currency: string) => void;
  proceedToPayment: (userId: string, options?: { openInNewTab?: boolean }) => Promise<void>;
  updateLicenseUsers: (
    subscriptionId: string,
    priceId: string,
    userIds: string[],
    userId: string,
  ) => Promise<boolean>;
  transferCredits: (
    emails: string,
    credits: number,
    userId: string,
  ) => Promise<string>;
  /**
   * `impersonateUserId` (admin only) is forwarded as `_ua_lic_impersonateUserID`
   * so the CF acts on that user's config instead of the caller's.
   */
  loadRenewalTransferConfig: (userId: string, impersonateUserId?: string | null) => Promise<void>;
  saveRenewalTransferConfig: (
    userId: string,
    config: RenewalCreditTransferConfig,
    impersonateUserId?: string | null,
  ) => Promise<{ success: boolean; message?: string }>;
  /** Support team only (impersonation): who may see/manage the viewed user's subscriptions. */
  loadSubscriptionDelegates: (userId: string, impersonateUserId: string) => Promise<void>;
  addSubscriptionDelegate: (
    userId: string,
    email: string,
    impersonateUserId: string,
  ) => Promise<{ success: boolean; message?: string }>;
  removeSubscriptionDelegate: (
    userId: string,
    delegateUid: string,
    impersonateUserId: string,
  ) => Promise<{ success: boolean; message?: string }>;
  /** `ownerUid` opens the portal of a delegated account instead of the user's own. */
  openBillingPortal: (ownerUid?: string) => Promise<void>;
  updateSubscriptionQuantity: (
    subscriptionId: string,
    newQuantity: number,
    userId: string,
  ) => Promise<{ status: string; paymentLink?: string; newQuantity: number }>;
  loadCRMUsers: (appCode: string) => Promise<void>;
  getOAuthURL: (appCode: string, userId: string) => string | null;
  filterProducts: (searchTerm: string) => void;
  cleanup: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TIMEZONE_CURRENCY_MAP: Record<string, string> = {
  'Asia/Kolkata': 'inr',
  'Asia/Calcutta': 'inr',
  'Asia/Chennai': 'inr',
  'Asia/Mumbai': 'inr',
  'Europe/London': 'gbp',
  'Australia/Sydney': 'aud',
  'Australia/Melbourne': 'aud',
  'Australia/Brisbane': 'aud',
  'Australia/Perth': 'aud',
  'Australia/Adelaide': 'aud',
  'Australia/Hobart': 'aud',
  'Australia/Darwin': 'aud',
  'Australia/Lord_Howe': 'aud',
};

function getCurrencyFromTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_CURRENCY_MAP[tz]) return TIMEZONE_CURRENCY_MAP[tz];
    if (tz.startsWith('Europe/')) return 'eur';
    return 'usd';
  } catch {
    return 'usd';
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useStoreStore = create<StoreState>((set, get) => ({
  // Initial state
  products: [],
  filteredProducts: [],
  prices: {},
  subscriptions: [],
  licenseOwners: {},
  credits: 0,
  creditHistory: [],
  renewalTransferConfig: null,
  renewalTransferLoadedFor: null,
  renewalTransferLoadError: null,
  _renewalTransferSeq: 0,

  subscriptionDelegates: [],
  subscriptionDelegatesLoadedFor: null,
  subscriptionDelegatesLoadError: null,
  _subscriptionDelegatesSeq: 0,
  savingSubscriptionDelegate: false,

  delegatedOwners: [],
  delegatedSubscriptions: [],
  delegatedLicenseOwners: {},
  loadingDelegated: false,
  delegatedLoaded: false,

  creditHistoryLastDoc: null,
  creditHistoryHasMore: true,

  selectedProductId: null,
  selectedPaymentPlanId: null,
  quantity: 1,
  selectedCurrency: getCurrencyFromTimezone(),
  searchTerm: '',

  loadingProducts: false,
  productsLoadedForApps: {},
  loadingSubscriptions: false,
  subscriptionsLoaded: false,
  subscriptionsLoadedFor: null,
  loadingCredits: false,
  loadingCreditHistory: false,
  loadingLicenseOwners: false,
  licenseOwnersLoadedFor: null,
  processingPayment: false,
  paymentPending: false,
  lastCheckoutUrl: null,
  processingLicenseUpdate: false,
  processingCreditTransfer: false,
  savingRenewalTransfer: false,
  processingPortal: false,

  crmUsers: {},
  crmUsersStatus: {},

  error: null,
  _creditsRef: null,
  _delegatedRequestId: 0,
  _delegatedImpersonUserId: null,

  // -----------------------------------------------------------------------
  // initialize
  // -----------------------------------------------------------------------
  initialize: async (userId: string, preferredCurrency?: string, options?: { impersonateUserId?: string | null }) => {
    try {
      set({ error: null });

      // Apply saved currency preference immediately if available
      if (preferredCurrency) {
        set({ selectedCurrency: preferredCurrency });
      }

      // Start credits listener immediately (real-time, non-blocking)
      get().loadCredits(userId);
      // Load subscriptions and license owners in parallel
      await Promise.all([
        get().loadSubscriptions(userId),
        get().loadLicenseOwners(userId),
        get().loadRenewalTransferConfig(userId, options?.impersonateUserId ?? null),
      ]);

      // If no saved preference, detect from active subscription or timezone
      if (!preferredCurrency) {
        get().detectCurrency();
      }
    } catch (err: any) {
      console.error('[store-store] initialize error:', err);
      set({ error: err?.message ?? 'Failed to initialize store' });
    }
  },

  // -----------------------------------------------------------------------
  // detectCurrency — auto-detect from active subscription, fallback to timezone
  // -----------------------------------------------------------------------
  detectCurrency: () => {
    const { subscriptions } = get();

    // Check active subscription's currency
    const activeSub = subscriptions.find(
      (s) => s.status === 'active' || s.status === 'trialing',
    );
    if (activeSub?.priceData?.currency) {
      set({ selectedCurrency: activeSub.priceData.currency });
      return;
    }

    // Fallback to timezone detection
    set({ selectedCurrency: getCurrencyFromTimezone() });
  },

  // -----------------------------------------------------------------------
  // loadProducts
  // -----------------------------------------------------------------------
  loadProducts: async (appCode?: string) => {
    // Dedupe: skip if a load for this appCode has already completed.
    // The `productsLoadedForApps` flag flips true after success OR error,
    // so subsequent calls (e.g. from useTrial in multiple components) are
    // no-ops and there's no infinite-retry loop when no product matches.
    const key = appCode ?? '';
    const state = get();
    if (state.productsLoadedForApps[key]) return;
    if (state.loadingProducts) return;
    set({ loadingProducts: true, error: null });
    try {
      const productsRef = collection(db, 'products');
      // When appCode is provided (in-app settings), only fetch the matching product
      const q = appCode
        ? query(productsRef, where('stripe_metadata_appcode', '==', appCode))
        : query(productsRef, where('active', '==', true), orderBy('stripe_metadata_payment_page_order', 'asc'));
      // Use raw getDocs for appCode queries to avoid onSnapshot cache + background sync
      // (prevents hundreds of billable reads from syncing all cached products/prices)
      const { getDocs: rawGetDocs } = await import('@/lib/firebase/firestore');
      const docsGet = appCode ? rawGetDocs : getDocs;
      const snapshot = await docsGet(q);

      const products: Product[] = [];
      const prices: Record<string, Price[]> = { ...get().prices };

      await Promise.all(
        snapshot.docs.map(async (productDoc) => {
          const data = productDoc.data();
          products.push({
            id: productDoc.id,
            name: data.name ?? '',
            description: data.description,
            images: data.images ?? [],
            active: data.active ?? true,
            ...data,
          });

          // Only fetch prices eagerly when appCode is provided (in-app settings, 1 product)
          // For store page (no appCode), prices are loaded lazily on product click
          if (appCode) {
            const pricesRef = collection(db, 'products', productDoc.id, 'prices');
            const pricesQuery = query(pricesRef, orderBy('unit_amount'));
            const pricesSnapshot = await docsGet(pricesQuery);

            prices[productDoc.id] = pricesSnapshot.docs.map((priceDoc) => {
              const priceData = priceDoc.data();
              return {
                id: priceDoc.id,
                productId: productDoc.id,
                active: priceData.active ?? true,
                currency: priceData.currency ?? 'usd',
                unit_amount: priceData.unit_amount ?? 0,
                type: priceData.type ?? 'recurring',
                interval: priceData.interval,
                interval_count: priceData.interval_count,
                description: priceData.description,
                trial_period_days: priceData.trial_period_days,
                nickname: priceData.description,
              } as Price;
            });
          }
        }),
      );

      set({
        products,
        filteredProducts: products,
        prices,
        loadingProducts: false,
        productsLoadedForApps: { ...get().productsLoadedForApps, [key]: true },
      });

      // Enrich any subscriptions that loaded before products were ready
      get().enrichSubscriptions();
    } catch (err: any) {
      console.error('[store-store] loadProducts error:', err);
      set({
        loadingProducts: false,
        productsLoadedForApps: { ...get().productsLoadedForApps, [key]: true },
        error: err?.message ?? 'Failed to load products',
      });
    }
  },

  // -----------------------------------------------------------------------
  // loadPricesForProduct — lazy load prices for a single product on click
  // -----------------------------------------------------------------------
  loadPricesForProduct: async (productId: string) => {
    const existing = get().prices[productId];
    if (existing && existing.length > 0) return; // already loaded
    try {
      const { getDocs: rawGetDocs } = await import('@/lib/firebase/firestore');
      const pricesRef = collection(db, 'products', productId, 'prices');
      const pricesQuery = query(pricesRef, orderBy('unit_amount'));
      const pricesSnapshot = await rawGetDocs(pricesQuery);

      const productPrices: Price[] = pricesSnapshot.docs.map((priceDoc) => {
        const priceData = priceDoc.data();
        return {
          id: priceDoc.id,
          productId,
          active: priceData.active ?? true,
          currency: priceData.currency ?? 'usd',
          unit_amount: priceData.unit_amount ?? 0,
          type: priceData.type ?? 'recurring',
          interval: priceData.interval,
          interval_count: priceData.interval_count,
          description: priceData.description,
          trial_period_days: priceData.trial_period_days,
          nickname: priceData.description,
        } as Price;
      });

      set({ prices: { ...get().prices, [productId]: productPrices } });
    } catch (err: any) {
      console.error('[store-store] loadPricesForProduct error:', err);
    }
  },

  // -----------------------------------------------------------------------
  // loadSubscriptions — fast, single query, no individual product/price lookups
  // -----------------------------------------------------------------------
  loadSubscriptions: async (userId: string) => {
    // Dedupe: skip if a load is already in flight, OR a load for THIS
    // SAME uid has already completed. Per-uid (not a global boolean)
    // so the dashboard / App Users impersonation flow can call
    // `loadSubscriptions(otherUid)` without being silently deduped
    // when an earlier `loadSubscriptions(currentUser.uid)` already
    // ran. useTrial / use-app-init still benefit from the dedupe
    // because they're called repeatedly with the same uid.
    const forceServer = sessionStorage.getItem('force_reload_subscriptions') === '1';
    if (!forceServer) {
      const state = get();
      if (state.loadingSubscriptions) return;
      if (state.subscriptionsLoadedFor === userId) return;
    }
    set({ loadingSubscriptions: true, error: null });
    try {
      const { getDocs: rawGetDocs, getDocsFromServer } = await import('@/lib/firebase/firestore');
      const subsRef = collection(db, 'customers', userId, 'subscriptions');
      const q = query(subsRef, orderBy('created', 'desc'));
      // Force server fetch if flagged (e.g. after adding licenses)
      if (forceServer) sessionStorage.removeItem('force_reload_subscriptions');
      const snapshot = forceServer ? await getDocsFromServer(q) : await rawGetDocs(q);

      const { products: cachedProducts, prices: cachedPrices } = get();

      const subscriptions: Subscription[] = snapshot.docs.map((subDoc) => {
        const data = subDoc.data();
        const sub: Subscription = {
          id: subDoc.id,
          status: data.status ?? '',
          quantity: data.quantity ?? 1,
          product: data.product,
          price: data.price,
          created: data.created,
          current_period_start: data.current_period_start,
          current_period_end: data.current_period_end,
          canceled_at: data.canceled_at,
        };

        // Extract IDs from Firestore references
        const productRefPath = typeof data.product === 'string' ? data.product : data.product?.path;
        const priceRefPath = typeof data.price === 'string' ? data.price : data.price?.path;
        const productId = productRefPath?.split('/')?.pop();
        const priceId = priceRefPath?.split('/')?.pop();

        if (productId) sub.productId = productId;
        if (priceId) sub.priceId = priceId;

        // Resolve from cache if products are already loaded
        const cachedProduct = productId ? cachedProducts.find((p) => p.id === productId) : null;
        const cachedPrice = productId && priceId ? cachedPrices[productId]?.find((p) => p.id === priceId) : null;

        if (cachedProduct) sub.productData = cachedProduct;
        if (cachedPrice) sub.priceData = cachedPrice;

        return sub;
      });

      // Show subscriptions immediately — product names/images fill in when loadProducts finishes
      set({ subscriptions, loadingSubscriptions: false, subscriptionsLoaded: true, subscriptionsLoadedFor: userId });

      // Hydrate priceData per-subscription in parallel. Matches legacy's
      // `subscription.price.get()` pattern — the generic `loadProducts` flow
      // only populates prices for products the user is browsing, so for
      // subscribed-only products `priceData` stays undefined without this.
      //
      // Dedupe by price path: multiple subs often share the same price doc
      // (e.g. 5 subs on the same plan). Fanning out one getDoc per sub against
      // identical paths trips the rate guard's per-path rapid-fire detector.
      void (async () => {
        try {
          const { getDoc } = await import('@/lib/firebase/firestore');
          const refs = snapshot.docs
            .map((subDoc) => {
              const raw = subDoc.data().price;
              const path = typeof raw === 'string' ? raw : raw?.path;
              return path ? { subId: subDoc.id, path } : null;
            })
            .filter((x): x is { subId: string; path: string } => !!x);

          const subIdsByPath = new Map<string, string[]>();
          for (const { subId, path } of refs) {
            const list = subIdsByPath.get(path);
            if (list) list.push(subId);
            else subIdsByPath.set(path, [subId]);
          }

          const priceByPath = new Map<string, Price>();
          await Promise.all(
            Array.from(subIdsByPath.keys()).map(async (path) => {
              try {
                const priceSnap = await getDoc(doc(db, path));
                if (!priceSnap.exists()) return;
                const p = priceSnap.data();
                priceByPath.set(path, {
                  id: priceSnap.id,
                  productId: path.split('/')[1] || '',
                  active: p.active ?? true,
                  currency: p.currency ?? 'usd',
                  unit_amount: p.unit_amount ?? 0,
                  type: p.type ?? 'recurring',
                  interval: p.interval,
                  interval_count: p.interval_count,
                  description: p.description,
                  trial_period_days: p.trial_period_days,
                  nickname: p.nickname,
                });
              } catch (err) {
                const subIds = subIdsByPath.get(path) ?? [];
                console.warn('[store-store] hydrate price failed for', subIds.join(','), err);
              }
            }),
          );

          if (priceByPath.size > 0) {
            const priceBySubId = new Map<string, Price>();
            for (const [path, price] of priceByPath) {
              for (const subId of subIdsByPath.get(path) ?? []) {
                priceBySubId.set(subId, price);
              }
            }
            set((state) => ({
              subscriptions: state.subscriptions.map((s) => {
                const price = priceBySubId.get(s.id);
                return price ? { ...s, priceData: price } : s;
              }),
            }));
          }
        } catch (err) {
          console.warn('[store-store] price hydration skipped:', err);
        }
      })();
    } catch (err: any) {
      console.error('[store-store] loadSubscriptions error:', err);
      set({
        loadingSubscriptions: false,
        subscriptionsLoaded: true,
        subscriptionsLoadedFor: userId,
        error: err?.message ?? 'Failed to load subscriptions',
      });
    }
  },

  // -----------------------------------------------------------------------
  // enrichSubscriptions — called after loadProducts to fill in product/price data
  // -----------------------------------------------------------------------
  enrichSubscriptions: () => {
    const { subscriptions, delegatedSubscriptions, products, prices } = get();
    if (products.length === 0) return;
    if (subscriptions.length === 0 && delegatedSubscriptions.length === 0) return;

    let changed = false;
    const enrich = (sub: Subscription) => {
      let updated = sub;
      if (!sub.productData && sub.productId) {
        const product = products.find((p) => p.id === sub.productId);
        if (product) {
          updated = { ...updated, productData: product };
          changed = true;
        }
      }
      if (!sub.priceData && sub.productId && sub.priceId) {
        const price = prices[sub.productId]?.find((p) => p.id === sub.priceId);
        if (price) {
          updated = { ...updated, priceData: price };
          changed = true;
        }
      }
      return updated;
    };

    // Delegated subs go through the same pass — they're rendered in the same
    // list, so they need product names and images just as much.
    const enriched = subscriptions.map(enrich);
    const enrichedDelegated = delegatedSubscriptions.map(enrich);

    if (changed) {
      set({ subscriptions: enriched, delegatedSubscriptions: enrichedDelegated });
    }
  },

  // -----------------------------------------------------------------------
  // loadCredits (real-time listener on RTDB)
  // -----------------------------------------------------------------------
  loadCredits: (userId: string) => {
    const prevRef = get()._creditsRef;
    if (prevRef) off(prevRef);

    set({ loadingCredits: true, error: null });

    const creditsRef = ref(rtdb, `ualic_user_credits/${userId}`);
    set({ _creditsRef: creditsRef });

    onValue(
      creditsRef,
      (snapshot) => {
        const value = snapshot.val();
        set({
          credits: typeof value === 'number' ? Math.ceil(value * 10) / 10 : 0,
          loadingCredits: false,
        });
      },
      (err: any) => {
        console.error('[store-store] loadCredits error:', err);
        set({ loadingCredits: false });
      },
    );
  },

  // -----------------------------------------------------------------------
  // loadCreditHistory (paginated, merges two collections)
  // -----------------------------------------------------------------------
  loadCreditHistory: async (userId: string, loadMore = false) => {
    set({ loadingCreditHistory: true, error: null });

    try {
      if (!loadMore) {
        set({ creditHistory: [], creditHistoryLastDoc: null, creditHistoryHasMore: true });
      }

      const existingHistory = loadMore ? get().creditHistory : [];

      // Collection 1: UALicenseOwners/{uid}/credit_adjustments
      const adjustmentsRef = collection(db, 'UALicenseOwners', userId, 'credit_adjustments');
      let adjQuery;
      if (loadMore && get().creditHistoryLastDoc) {
        adjQuery = query(adjustmentsRef, orderBy('ct', 'desc'), startAfter(get().creditHistoryLastDoc), limit(PAGE_SIZE));
      } else {
        adjQuery = query(adjustmentsRef, orderBy('ct', 'desc'), limit(PAGE_SIZE));
      }
      const adjSnapshot = await getDocs(adjQuery);

      // Collection 2: UALICTOWCUserCreditActivity/{uid}/activities
      const activitiesRef = collection(db, 'UALICTOWCUserCreditActivity', userId, 'activities');
      let activitiesQuery;
      if (loadMore && get().creditHistoryLastDoc) {
        activitiesQuery = query(activitiesRef, orderBy('ct', 'desc'), startAfter(get().creditHistoryLastDoc), limit(PAGE_SIZE));
      } else {
        activitiesQuery = query(activitiesRef, orderBy('ct', 'desc'), limit(PAGE_SIZE));
      }
      const activitiesSnapshot = await getDocs(activitiesQuery);

      const toCreditActivity = (d: any): CreditActivity => {
        const data = d.data();
        return {
          ct: data.ct ?? 0,
          old: data.old ?? 0,
          new: data.new ?? 0,
          type: data.type,
          hint: data.hint,
          oldStatus: data.oldStatus,
          newStatus: data.newStatus,
          oldSub: data.oldSub,
          newSub: data.newSub,
        };
      };

      const adjustments = adjSnapshot.docs.map(toCreditActivity);
      const activities = activitiesSnapshot.docs.map(toCreditActivity);

      // Determine last doc for pagination
      const allDocs = [...adjSnapshot.docs, ...activitiesSnapshot.docs];
      const newLastDoc = allDocs.length > 0 ? allDocs[allDocs.length - 1] : get().creditHistoryLastDoc;

      // Merge, deduplicate by ct+old+new, sort descending
      const merged = [...existingHistory, ...adjustments, ...activities];
      const seen = new Set<string>();
      const deduped: CreditActivity[] = [];
      for (const item of merged) {
        const key = `${item.ct}-${item.old}-${item.new}`;
        // Monthly reset entries stay visible even when old === new (a reset
        // from a 0 balance) — the reset itself is the event being shown.
        if (!seen.has(key) && (Number(item.old) !== Number(item.new) || item.type === 'monthly_credit_reset')) {
          seen.add(key);
          deduped.push(item);
        }
      }
      deduped.sort((a, b) => b.ct - a.ct);

      set({
        creditHistory: deduped,
        creditHistoryLastDoc: newLastDoc,
        creditHistoryHasMore: adjSnapshot.docs.length === PAGE_SIZE || activitiesSnapshot.docs.length === PAGE_SIZE,
        loadingCreditHistory: false,
      });
    } catch (err: any) {
      console.error('[store-store] loadCreditHistory error:', err);
      set({ loadingCreditHistory: false, error: err?.message ?? 'Failed to load credit history' });
    }
  },

  // -----------------------------------------------------------------------
  // loadLicenseOwners
  // -----------------------------------------------------------------------
  loadLicenseOwners: async (userId: string) => {
    // Dedupe per-uid: if this exact uid has already loaded (or is loading),
    // skip. Without this, callers that gate on
    // `Object.keys(licenseOwners).length` (which stays 0 for users with no
    // allocations) refetch in a loop every time the empty `{}` reference
    // changes. Per-uid (not global) so dashboard impersonation can still
    // load a different user's data.
    const state = get();
    if (state.loadingLicenseOwners) return;
    if (state.licenseOwnersLoadedFor === userId) return;
    set({ loadingLicenseOwners: true, error: null });
    try {
      const subsRef = collection(db, 'UALicenseOwners', userId, 'subscriptions');
      const snapshot = await getDocs(subsRef);

      const licenseOwners: Record<string, LicenseOwner> = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        licenseOwners[d.id] = { userIds: data.userIds ?? [] };
      });

      set({ licenseOwners, loadingLicenseOwners: false, licenseOwnersLoadedFor: userId });
    } catch (err: any) {
      console.error('[store-store] loadLicenseOwners error:', err);
      set({
        loadingLicenseOwners: false,
        licenseOwnersLoadedFor: userId,
        error: err?.message ?? 'Failed to load license owners',
      });
    }
  },

  // -----------------------------------------------------------------------
  // loadDelegatedAccess — subscriptions of OTHER accounts granted to this user
  //
  // Grants live in `UASubscriptionDelegates/{ownerUid}` and are resolved by the
  // `getDelegatedSubscriptionOwners` Cloud Function, which reads the caller's
  // email from Firebase Auth (never from anything the browser sends). The list
  // returned here drives rendering only — the server re-verifies the grant on
  // every license save and seat purchase, so a tampered client still can't
  // reach an account it wasn't granted.
  // -----------------------------------------------------------------------
  loadDelegatedAccess: async (impersonateUserId?: string | null) => {
    if (get().loadingDelegated) return;
    const requestId = get()._delegatedRequestId + 1;
    set({
      loadingDelegated: true,
      _delegatedRequestId: requestId,
      _delegatedImpersonUserId: impersonateUserId ?? null,
    });

    // True once `cleanup` or a newer load has superseded this one.
    const isStale = () => get()._delegatedRequestId !== requestId;

    const clear = () => {
      if (isStale()) return;
      set({
        delegatedOwners: [],
        delegatedSubscriptions: [],
        delegatedLicenseOwners: {},
        loadingDelegated: false,
        delegatedLoaded: true,
      });
    };

    try {
      const getOwners = httpsCallable(functions, 'getDelegatedSubscriptionOwners');
      // Support impersonation on the store page: ask for the impersonated
      // account's grants. The server only honours this for support uids.
      const result = await getOwners(impersonateUserId ? { impersonUserId: impersonateUserId } : {});
      const owners = ((result.data as { owners?: DelegatedOwner[] } | null)?.owners ?? []).filter(
        (o) => !!o?.ownerUid,
      );
      if (owners.length === 0) {
        clear();
        return;
      }

      const { products: cachedProducts, prices: cachedPrices } = get();
      const subscriptions: Subscription[] = [];
      const licenseOwners: Record<string, LicenseOwner> = {};
      // subId -> price doc path, for the hydration pass below
      const pricePathBySubId = new Map<string, string>();

      await Promise.all(
        owners.map(async (owner) => {
          // A single failing owner must not blank out the others.
          try {
            const snapshot = await getDocs(
              query(
                collection(db, 'customers', owner.ownerUid, 'subscriptions'),
                orderBy('created', 'desc'),
              ),
            );
            snapshot.docs.forEach((subDoc: QueryDocumentSnapshot) => {
              const data = subDoc.data();
              const productRefPath =
                typeof data.product === 'string' ? data.product : data.product?.path;
              const priceRefPath = typeof data.price === 'string' ? data.price : data.price?.path;
              const productId = productRefPath?.split('/')?.pop();
              const priceId = priceRefPath?.split('/')?.pop();

              const sub: Subscription = {
                id: subDoc.id,
                status: data.status ?? '',
                quantity: data.quantity ?? 1,
                product: data.product,
                price: data.price,
                created: data.created,
                current_period_start: data.current_period_start,
                current_period_end: data.current_period_end,
                canceled_at: data.canceled_at,
                ownerUid: owner.ownerUid,
                ownerEmail: owner.ownerEmail,
              };
              if (productId) sub.productId = productId;
              if (priceId) sub.priceId = priceId;

              const cachedProduct = productId
                ? cachedProducts.find((p) => p.id === productId)
                : null;
              if (cachedProduct) sub.productData = cachedProduct;
              const cachedPrice =
                productId && priceId
                  ? cachedPrices[productId]?.find((p) => p.id === priceId)
                  : null;
              if (cachedPrice) sub.priceData = cachedPrice;

              if (!sub.priceData && priceRefPath) pricePathBySubId.set(sub.id, priceRefPath);
              subscriptions.push(sub);
            });
          } catch (err) {
            console.warn('[store-store] delegated subs load failed for', owner.ownerUid, err);
          }

          try {
            const licSnapshot = await getDocs(
              collection(db, 'UALicenseOwners', owner.ownerUid, 'subscriptions'),
            );
            licSnapshot.docs.forEach((d: QueryDocumentSnapshot) => {
              licenseOwners[d.id] = { userIds: d.data().userIds ?? [] };
            });
          } catch (err) {
            console.warn('[store-store] delegated licenses load failed for', owner.ownerUid, err);
          }
        }),
      );

      if (isStale()) return;
      set({
        delegatedOwners: owners,
        delegatedSubscriptions: subscriptions,
        delegatedLicenseOwners: licenseOwners,
        loadingDelegated: false,
        delegatedLoaded: true,
      });

      // Hydrate missing priceData. Deduped by price path — subs on the same
      // plan share one price doc, and fanning out identical getDoc calls trips
      // the rate guard's per-path rapid-fire detector.
      if (pricePathBySubId.size > 0) {
        void (async () => {
          try {
            const { getDoc } = await import('@/lib/firebase/firestore');
            const subIdsByPath = new Map<string, string[]>();
            for (const [subId, path] of pricePathBySubId) {
              const list = subIdsByPath.get(path);
              if (list) list.push(subId);
              else subIdsByPath.set(path, [subId]);
            }

            const priceBySubId = new Map<string, Price>();
            await Promise.all(
              Array.from(subIdsByPath.keys()).map(async (path) => {
                try {
                  const priceSnap = await getDoc(doc(db, path));
                  if (!priceSnap.exists()) return;
                  const p = priceSnap.data();
                  const price: Price = {
                    id: priceSnap.id,
                    productId: path.split('/')[1] || '',
                    active: p.active ?? true,
                    currency: p.currency ?? 'usd',
                    unit_amount: p.unit_amount ?? 0,
                    type: p.type ?? 'recurring',
                    interval: p.interval,
                    description: p.description,
                    trial_period_days: p.trial_period_days,
                    nickname: p.nickname,
                  };
                  for (const subId of subIdsByPath.get(path) ?? []) priceBySubId.set(subId, price);
                } catch (err) {
                  console.warn('[store-store] delegated price hydrate failed for', path, err);
                }
              }),
            );

            if (priceBySubId.size > 0 && !isStale()) {
              set((state) => ({
                delegatedSubscriptions: state.delegatedSubscriptions.map((s) => {
                  const price = priceBySubId.get(s.id);
                  return price ? { ...s, priceData: price } : s;
                }),
              }));
            }
          } catch (err) {
            console.warn('[store-store] delegated price hydration skipped:', err);
          }
        })();
      }
    } catch (err) {
      // No grant / function unavailable is the normal case for most users —
      // stay quiet and simply show nothing extra.
      console.warn('[store-store] loadDelegatedAccess skipped:', err);
      clear();
    }
  },

  // -----------------------------------------------------------------------
  // UI Actions
  // -----------------------------------------------------------------------
  selectProduct: (productId: string | null) => {
    set({ selectedProductId: productId, selectedPaymentPlanId: null });
    if (productId) {
      get().loadPricesForProduct(productId);
    }
  },

  selectPaymentPlan: (priceId: string | null) => {
    set({ selectedPaymentPlanId: priceId });
  },

  setQuantity: (quantity: number) => {
    set({ quantity: Math.max(1, quantity) });
  },

  setCurrency: (currency: string) => {
    set({ selectedCurrency: currency });
  },

  // -----------------------------------------------------------------------
  // proceedToPayment
  // -----------------------------------------------------------------------
  proceedToPayment: async (userId: string, options?: { openInNewTab?: boolean }) => {
    const openInNewTab = options?.openInNewTab ?? false;
    const { selectedProductId, selectedPaymentPlanId, quantity, selectedCurrency } = get();

    if (!selectedProductId || !selectedPaymentPlanId) {
      set({ error: 'Please select a product and payment plan' });
      return;
    }

    set({ processingPayment: true, error: null });
    try {
      const taxRates = selectedCurrency === 'inr' ? TAX_RATES_INR : TAX_RATES_OTHER;

      // Disable trial for users who already have subscriptions (matches old app behavior)
      const hasExistingSubscriptions = get().subscriptions.length > 0;

      // Save currency preference for future visits (fire-and-forget)
      setDoc(doc(db, 'ulgebraUsers', userId), { preferredCurrency: selectedCurrency }, { merge: true }).catch(() => {});

      // Write to Firestore checkout_sessions — Stripe Firebase Extension picks it up
      // and creates a Checkout Session, then writes the sessionId back to the document.
      const firebase = await import('@/lib/firebase/firestore');
      console.log('[store-store] Creating checkout session for', userId);
      const docRef = await firebase.addDoc(firebase.collection(db, 'customers', userId, 'checkout_sessions'), {
        price: selectedPaymentPlanId,
        allow_promotion_codes: true,
        tax_rates: taxRates,
        success_url: `${window.location.origin}/store`,
        cancel_url: `${window.location.origin}/store`,
        quantity,
        trial_from_plan: !hasExistingSubscriptions,
        metadata: {
          tax_rate: taxRates.length > 0 ? '18% GST exclusive' : '',
        },
      });

      // Wait for the Stripe Extension to attach sessionId
      console.log('[store-store] Listening for checkout session:', docRef.id);
      firebase.onSnapshot(docRef, (snap) => {
        const data = snap.data() as { error?: { message: string }; sessionId?: string; url?: string } | undefined;
        console.log('[store-store] Checkout session update:', data);
        if (data?.error) {
          set({ processingPayment: false, error: data.error.message });
          return;
        }
        // Newer Stripe Extension returns a URL directly
        if (data?.url) {
          if (openInNewTab) {
            openWindowSafe(data.url);
            set({ processingPayment: false, paymentPending: true, lastCheckoutUrl: data.url });
          } else {
            window.location.assign(data.url);
          }
          return;
        }
        if (data?.sessionId) {
          // Load Stripe.js dynamically if not already loaded
          const initStripe = async () => {
            if (!(window as any).Stripe) {
              await new Promise<void>((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://js.stripe.com/v3/';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load Stripe.js'));
                document.head.appendChild(script);
              });
            }
            const stripe = (window as any).Stripe(STRIPE_PUBLISHABLE_KEY);
            if (openInNewTab) {
              // Can't use redirectToCheckout in new tab — construct URL manually
              const checkoutUrl = `https://checkout.stripe.com/pay/${data.sessionId}`;
              openWindowSafe(checkoutUrl);
              set({ processingPayment: false, paymentPending: true, lastCheckoutUrl: checkoutUrl });
            } else {
              stripe.redirectToCheckout({ sessionId: data.sessionId });
            }
          };
          initStripe().catch((err) => {
            set({ processingPayment: false, error: err.message });
          });
        }
      });
    } catch (err: any) {
      console.error('[store-store] proceedToPayment error:', err);
      set({ processingPayment: false, error: err?.message ?? 'Failed to create checkout session' });
    }
  },

  // -----------------------------------------------------------------------
  // updateLicenseUsers
  // -----------------------------------------------------------------------
  updateLicenseUsers: async (subscriptionId: string, priceId: string, userIds: string[], userId: string): Promise<boolean> => {
    const cleaned = userIds.map((u) => u.trim());
    set({ processingLicenseUpdate: true, error: null });
    try {
      const saveLICUsersList = httpsCallable(functions, 'saveLICUsersList');
      const result = await saveLICUsersList({
        subscriptionId,
        priceId,
        userIds: cleaned,
        impersonUserId: userId,
      });
      const response = (result.data as string) ?? '';

      if (response !== 'end of process') {
        set({ processingLicenseUpdate: false, error: response || 'Failed to save license users' });
        return false;
      }

      // Write the result back into whichever map holds this sub, so a
      // delegated save isn't mirrored onto the user's own allocations.
      set((state) => {
        const isDelegated = state.delegatedSubscriptions.some((s) => s.id === subscriptionId);
        return isDelegated
          ? {
              delegatedLicenseOwners: {
                ...state.delegatedLicenseOwners,
                [subscriptionId]: { userIds: cleaned },
              },
              processingLicenseUpdate: false,
            }
          : {
              licenseOwners: {
                ...state.licenseOwners,
                [subscriptionId]: { userIds: cleaned },
              },
              processingLicenseUpdate: false,
            };
      });
      return true;
    } catch (err: any) {
      console.error('[store-store] updateLicenseUsers error:', err);
      set({ processingLicenseUpdate: false, error: err?.message ?? 'Failed to update license users' });
      return false;
    }
  },

  // -----------------------------------------------------------------------
  // transferCredits
  // -----------------------------------------------------------------------
  transferCredits: async (emails: string, credits: number, userId: string) => {
    set({ processingCreditTransfer: true, error: null });
    try {
      // transferCredits CF may not be routed through the custom domain — use default endpoint
      const { getFunctions } = await import('firebase/functions');
      const { app } = await import('@/config/firebase');
      const defaultFunctions = getFunctions(app);
      const transferCreditsFn = httpsCallable(defaultFunctions, 'transferCreditsToAnotherUlgebraAccount');

      // Old app sends one email at a time with { transferCreditToEmail, transferCreditCount }
      const emailList = emails.split(/[,\s]+/).map(e => e.trim()).filter(Boolean);
      const results: string[] = [];
      for (const mailItem of emailList) {
        const result = await transferCreditsFn({ transferCreditToEmail: mailItem, transferCreditCount: credits });
        results.push((result.data as any)?.message ?? `Transferred to ${mailItem}`);
      }
      set({ processingCreditTransfer: false });
      return results.join('; ');
    } catch (err: any) {
      console.error('[store-store] transferCredits error:', err);
      set({ processingCreditTransfer: false, error: err?.message ?? 'Failed to transfer credits' });
      throw err;
    }
  },

  // -----------------------------------------------------------------------
  // loadRenewalTransferConfig — via new_callServiceAuthorizedAPI (deployed
  // Firestore rules don't allow direct client access to the subcollection).
  // The server acts on the CALLER's uid, so this only loads the signed-in
  // user's own config (the section is hidden in admin impersonation view).
  // -----------------------------------------------------------------------
  loadRenewalTransferConfig: async (userId: string, impersonateUserId?: string | null) => {
    // Impersonation switches call this back-to-back for different uids; only
    // the most recent call may write its result (the CF has no cancel).
    const seq = get()._renewalTransferSeq + 1;
    set({ _renewalTransferSeq: seq, renewalTransferLoadError: null });
    try {
      const result = await callServiceAPI({
        appCode: 'storeforulgebra',
        TPAService: 'storeUlgebra',
        url: 'get_credits_transfer_to',
        method: 'get',
        data: {},
        ...(impersonateUserId ? { _ua_lic_impersonateUserID: impersonateUserId } : {}),
      });
      if (get()._renewalTransferSeq !== seq) return;
      const data = result.data as {
        status?: string;
        message?: string;
        config?: { enabled?: boolean; transferToEmail?: string } | null;
      };
      if (data?.status !== 'success') {
        // A failed read must NOT look like "not configured": auto-transfer may
        // still be active server-side. Leave loadedFor unset so the section
        // shows a retry instead of a false "Off".
        set({
          renewalTransferConfig: null,
          renewalTransferLoadedFor: null,
          renewalTransferLoadError: data?.message || 'Could not load the credits auto-transfer setting',
        });
        return;
      }
      const config = data.config
        ? { enabled: !!data.config.enabled, transferToEmail: data.config.transferToEmail ?? '' }
        : null;
      set({ renewalTransferConfig: config, renewalTransferLoadedFor: userId, renewalTransferLoadError: null });
    } catch (err: any) {
      if (get()._renewalTransferSeq !== seq) return;
      console.error('[store-store] loadRenewalTransferConfig error:', err);
      set({
        renewalTransferConfig: null,
        renewalTransferLoadedFor: null,
        renewalTransferLoadError: err?.message || 'Could not load the credits auto-transfer setting',
      });
    }
  },

  // -----------------------------------------------------------------------
  // saveRenewalTransferConfig — via new_callServiceAuthorizedAPI; the server
  // also verifies the target Ulgebra account exists, so a failed save
  // carries a user-facing message.
  // -----------------------------------------------------------------------
  saveRenewalTransferConfig: async (
    _userId: string,
    config: RenewalCreditTransferConfig,
    impersonateUserId?: string | null,
  ) => {
    set({ savingRenewalTransfer: true, error: null });
    try {
      const result = await callServiceAPI({
        appCode: 'storeforulgebra',
        TPAService: 'storeUlgebra',
        url: 'save_credits_transfer_to',
        method: 'post',
        data: {
          enabled: config.enabled,
          transferToEmail: config.transferToEmail,
        },
        ...(impersonateUserId ? { _ua_lic_impersonateUserID: impersonateUserId } : {}),
      });
      const data = result.data as { status?: string; message?: string };
      if (data?.status !== 'success') {
        set({ savingRenewalTransfer: false });
        return { success: false, message: data?.message };
      }
      set({
        renewalTransferConfig: { ...config },
        renewalTransferLoadedFor: _userId,
        renewalTransferLoadError: null,
        savingRenewalTransfer: false,
      });
      return { success: true };
    } catch (err: any) {
      console.error('[store-store] saveRenewalTransferConfig error:', err);
      set({ savingRenewalTransfer: false });
      return { success: false, message: err?.message };
    }
  },

  loadSubscriptionDelegates: async (userId: string, impersonateUserId: string) => {
    const seq = get()._subscriptionDelegatesSeq + 1;
    set({ _subscriptionDelegatesSeq: seq, subscriptionDelegatesLoadError: null });
    try {
      const result = await callServiceAPI({
        appCode: 'storeforulgebra',
        TPAService: 'storeUlgebra',
        url: 'get_subscription_delegates',
        method: 'get',
        data: {},
        _ua_lic_impersonateUserID: impersonateUserId,
      });
      if (get()._subscriptionDelegatesSeq !== seq) return;
      const data = result.data as { status?: string; message?: string; delegates?: SubscriptionDelegate[] };
      if (data?.status !== 'success') {
        set({
          subscriptionDelegates: [],
          subscriptionDelegatesLoadedFor: null,
          subscriptionDelegatesLoadError: data?.message || 'Could not load delegate access',
        });
        return;
      }
      set({
        subscriptionDelegates: data.delegates ?? [],
        subscriptionDelegatesLoadedFor: userId,
        subscriptionDelegatesLoadError: null,
      });
    } catch (err: any) {
      if (get()._subscriptionDelegatesSeq !== seq) return;
      console.error('[store-store] loadSubscriptionDelegates error:', err);
      set({
        subscriptionDelegates: [],
        subscriptionDelegatesLoadedFor: null,
        subscriptionDelegatesLoadError: err?.message || 'Could not load delegate access',
      });
    }
  },

  addSubscriptionDelegate: async (_userId: string, email: string, impersonateUserId: string) => {
    set({ savingSubscriptionDelegate: true });
    try {
      const result = await callServiceAPI({
        appCode: 'storeforulgebra',
        TPAService: 'storeUlgebra',
        url: 'add_subscription_delegate',
        method: 'post',
        data: { email },
        _ua_lic_impersonateUserID: impersonateUserId,
      });
      const data = result.data as { status?: string; message?: string; delegate?: SubscriptionDelegate };
      set({ savingSubscriptionDelegate: false });
      if (data?.status !== 'success' || !data.delegate) {
        return { success: false, message: data?.message };
      }
      const added = data.delegate;
      set({
        subscriptionDelegates: [
          ...get().subscriptionDelegates.filter((d) => d.uid !== added.uid),
          added,
        ],
      });
      return { success: true };
    } catch (err: any) {
      console.error('[store-store] addSubscriptionDelegate error:', err);
      set({ savingSubscriptionDelegate: false });
      return { success: false, message: err?.message };
    }
  },

  removeSubscriptionDelegate: async (_userId: string, delegateUid: string, impersonateUserId: string) => {
    set({ savingSubscriptionDelegate: true });
    try {
      const result = await callServiceAPI({
        appCode: 'storeforulgebra',
        TPAService: 'storeUlgebra',
        url: 'remove_subscription_delegate',
        method: 'post',
        data: { uid: delegateUid },
        _ua_lic_impersonateUserID: impersonateUserId,
      });
      const data = result.data as { status?: string; message?: string };
      set({ savingSubscriptionDelegate: false });
      if (data?.status !== 'success') {
        return { success: false, message: data?.message };
      }
      set({ subscriptionDelegates: get().subscriptionDelegates.filter((d) => d.uid !== delegateUid) });
      return { success: true };
    } catch (err: any) {
      console.error('[store-store] removeSubscriptionDelegate error:', err);
      set({ savingSubscriptionDelegate: false });
      return { success: false, message: err?.message };
    }
  },

  // -----------------------------------------------------------------------
  // openBillingPortal
  // -----------------------------------------------------------------------
  openBillingPortal: async (ownerUid?: string) => {
    set({ processingPortal: true, error: null });
    try {
      let result;
      if (ownerUid) {
        // The Stripe extension's createPortalLink always uses the caller's own
        // uid, so a delegated account needs our own endpoint — it verifies the
        // grant server-side before resolving the owner's Stripe customer.
        const createDelegatedPortalLink = httpsCallable(functions, 'createDelegatedPortalLink');
        result = await createDelegatedPortalLink({
          ownerUid,
          returnUrl: window.location.origin + '/store',
        });
      } else {
        // Stripe Extension function — use default endpoint (not custom domain)
        const { getFunctions } = await import('firebase/functions');
        const { app } = await import('@/config/firebase');
        const defaultFunctions = getFunctions(app);
        const createPortalLink = httpsCallable(
          defaultFunctions,
          'ext-firestore-stripe-subscriptions-createPortalLink',
        );
        result = await createPortalLink({
          returnUrl: window.location.origin + '/store',
        });
      }

      const data = result.data as { url?: string };
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Invalid portal response');
      }
    } catch (err: any) {
      console.error('[store-store] openBillingPortal error:', err);
      set({ processingPortal: false, error: err?.message ?? 'Failed to open billing portal' });
    }
  },

  // -----------------------------------------------------------------------
  // updateSubscriptionQuantity
  // -----------------------------------------------------------------------
  updateSubscriptionQuantity: async (subscriptionId: string, newQuantity: number, userId: string) => {
    try {
      const updateQtyFn = httpsCallable(functions, 'updateSubscriptionQuantity');
      // `impersonUserId` names the account that owns the sub — the signed-in
      // user when it's their own, the granting account for a delegated one.
      // The server ignores it unless the caller is support or a verified
      // delegate, so sending it unconditionally is safe.
      const result = await updateQtyFn({ subscriptionId, newQuantity, impersonUserId: userId });
      const data = result.data as { status: string; paymentLink?: string; newQuantity: number };

      // Reload subscriptions to reflect updated quantity
      const isDelegated = get().delegatedSubscriptions.some((s) => s.id === subscriptionId);
      if (isDelegated) {
        await get().loadDelegatedAccess(get()._delegatedImpersonUserId);
      } else {
        await get().loadSubscriptions(userId);
      }

      return data;
    } catch (err: any) {
      console.error('[store-store] updateSubscriptionQuantity error:', err);
      throw err;
    }
  },

  // -----------------------------------------------------------------------
  // loadCRMUsers — fetch CRM team members for license email suggestions
  // -----------------------------------------------------------------------
  loadCRMUsers: async (appCode: string) => {
    // Extract saas portion: "twilioforzohocrm" → "zohocrm"
    const forIdx = appCode.indexOf('for');
    if (forIdx < 0) return;
    const rawSaas = appCode.substring(forIdx + 3);
    // Resolve aliases: e.g. "salesporcecrm" → "salesforce", "salesforcecrm" → "salesforce"
    const saas = SAAS_ALIASES[rawSaas] || rawSaas;

    const currentStatus = get().crmUsersStatus[saas];
    if (currentStatus === 'loading' || currentStatus === 'loaded') return;

    const config = CRM_USERS_CONFIG[saas];
    if (!config) {
      set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'no_connection' } }));
      return;
    }

    set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'loading' } }));

    try {
      // Special path: use saasModule.getOrgUsers() for platforms that need OAuth proxy
      // with dynamic params (e.g. Zoho Books requires organization_id + domain).
      // The old app calls these via UA_OAUTH_PROCESSOR.getAPIResponse which goes through
      // the same proxy. The SaaS module's getOrgUsers() handles this correctly with
      // pagination, organization_id, and the right domain.
      if (config.url === '__use_saas_module__') {
        const { useModuleStore } = await import('@/stores/module-store');
        const saasModule = useModuleStore.getState().saasModule;
        if (saasModule?.getOrgUsers) {
          const saasUsers = await saasModule.getOrgUsers();
          const users = config.normalize(saasUsers).sort((a, b) => a.name.localeCompare(b.name));
          if (users.length === 0) {
            set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'error' } }));
            return;
          }
          set((s) => ({
            crmUsers: { ...s.crmUsers, [saas]: users },
            crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'loaded' },
          }));
          return;
        }
      }

      const resolvedUrl = typeof config.url === 'function' ? config.url() : config.url;
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      const result = await callServiceAPI({
        appCode,
        url: resolvedUrl,
        method: config.method ? config.method : 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: config.authHeader,
        },
        // Include POST body when config provides data (e.g. Monday.com GraphQL)
        ...(config.data ? { data: config.data } : {}),
      });
      const raw = result.data as any;
      // Cloud Function may wrap the CRM API response — check for auth errors
      const statusCode = raw?.statusCode ?? raw?.data?.statusCode ?? 200;
      if (statusCode === 401 || statusCode === 403 || raw?.error) {
        set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'error' } }));
        return;
      }
      const responseData = raw?.data ?? raw;
      const users = config.normalize(responseData).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      if (users.length === 0) {
        set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'error' } }));
        return;
      }

      set((s) => ({
        crmUsers: { ...s.crmUsers, [saas]: users },
        crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'loaded' },
      }));
    } catch (err) {
      console.warn('[store-store] loadCRMUsers error:', err);
      set((s) => ({ crmUsersStatus: { ...s.crmUsersStatus, [saas]: 'error' } }));
    }
  },

  // -----------------------------------------------------------------------
  // getOAuthURL — build OAuth authorization URL for a CRM
  // -----------------------------------------------------------------------
  getOAuthURL: (appCode: string, userId: string) => {
    const forIdx = appCode.indexOf('for');
    if (forIdx < 0) return null;
    const saas = appCode.substring(forIdx + 3);
    const config = OAUTH_AUTH_CONFIG[saas];
    if (!config) return null;

    const clientId = config.clientIdMap[appCode] || config.clientIdMap['*'];
    if (!clientId) return null;

    // Build redirect_uri to match original patterns per CRM
    let redirectUri: string;
    if (config.includeClientIdInRedirect) {
      // Pipedrive: redirect_uri includes client_id + appCode
      redirectUri = `${config.redirectUri}?client_id=${clientId}&appCode=${appCode}`;
    } else if (saas === 'zohocrm') {
      // Zoho: redirect_uri has no query params
      redirectUri = config.redirectUri;
    } else {
      // HubSpot: redirect_uri includes appCode
      redirectUri = `${config.redirectUri}?appCode=${appCode}`;
    }

    // Build URL matching original per-CRM patterns
    if (saas === 'bitrix24') {
      // Bitrix24 OAuth requires the user's domain (e.g. mycompany.bitrix24.com) which
      // is not available from the store page. Authorization must happen from the app's
      // SaaS auth prompt which has a domain input field.
      return null;
    }
    if (saas === 'monday') {
      // Monday.com: no redirect_uri, no response_type, no scope — just client_id and state
      // Without subdomain the user will select their workspace on Monday's auth page
      return `${config.authUrl}?client_id=${clientId}&state=${userId}:::${appCode}`;
    }
    if (saas === 'pipedrive') {
      // Pipedrive: redirect_uri must be URL-encoded, no scope/access_type/prompt
      return `${config.authUrl}?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&state=${userId}:::${appCode}`;
    }
    if (saas === 'hubspotcrm') {
      // HubSpot: scope is space-separated, no access_type/prompt
      const scopes = config.scopeOverrides?.[appCode] ?? config.scopes;
      const scope = scopes ? `&scope=${encodeURIComponent(scopes)}` : '';
      return `${config.authUrl}?client_id=${clientId}&redirect_uri=${redirectUri}${scope}&state=${userId}:::${appCode}`;
    }
    // Zoho and others: use URLSearchParams with access_type + prompt
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      ...(config.scopes ? { scope: config.scopes } : {}),
      redirect_uri: redirectUri,
      state: `${userId}:::${appCode}`,
      access_type: 'offline',
      prompt: 'consent',
    });
    return `${config.authUrl}?${params.toString()}`;
  },

  // -----------------------------------------------------------------------
  // filterProducts
  // -----------------------------------------------------------------------
  filterProducts: (searchTerm: string) => {
    const { products } = get();
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      set({ filteredProducts: products, searchTerm: '' });
      return;
    }

    const filtered = products.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term)) ||
        (p.stripe_metadata_company && p.stripe_metadata_company.toLowerCase().includes(term)) ||
        (p.stripe_metadata_appcode && p.stripe_metadata_appcode.toLowerCase().includes(term)),
    );

    set({ filteredProducts: filtered, searchTerm });
  },

  // -----------------------------------------------------------------------
  // cleanup
  // -----------------------------------------------------------------------
  cleanup: () => {
    const creditsRef = get()._creditsRef;
    if (creditsRef) off(creditsRef);

    // Only clear user-specific data; products & prices are public and persist
    set({
      subscriptions: [],
      licenseOwners: {},
      delegatedOwners: [],
      delegatedSubscriptions: [],
      delegatedLicenseOwners: {},
      loadingDelegated: false,
      delegatedLoaded: false,
      credits: 0,
      creditHistory: [],
      renewalTransferConfig: null,
      renewalTransferLoadedFor: null,
      renewalTransferLoadError: null,
      _renewalTransferSeq: get()._renewalTransferSeq + 1,
      savingRenewalTransfer: false,
      subscriptionDelegates: [],
      subscriptionDelegatesLoadedFor: null,
      subscriptionDelegatesLoadError: null,
      _subscriptionDelegatesSeq: get()._subscriptionDelegatesSeq + 1,
      savingSubscriptionDelegate: false,
      creditHistoryLastDoc: null,
      creditHistoryHasMore: true,
      selectedProductId: null,
      selectedPaymentPlanId: null,
      quantity: 1,
      loadingSubscriptions: false,
      subscriptionsLoaded: false,
      subscriptionsLoadedFor: null,
      loadingCredits: false,
      loadingCreditHistory: false,
      loadingLicenseOwners: false,
      processingPayment: false,
      processingLicenseUpdate: false,
      processingCreditTransfer: false,
      processingPortal: false,
      crmUsers: {},
      crmUsersStatus: {},
      error: null,
      _creditsRef: null,
      _delegatedRequestId: get()._delegatedRequestId + 1,
      _delegatedImpersonUserId: null,
    });
  },
}));

// ---------------------------------------------------------------------------
// Exported helpers
// ---------------------------------------------------------------------------

export { STRIPE_PUBLISHABLE_KEY, TAX_RATES_INR, TAX_RATES_OTHER, CURRENCY_MAP };
